import { Fragment, useEffect, useState, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import translator from '@Components/Utilities/Translator';
import { sandhillsRequest, getURLParameter } from '@Components/Utilities/SandhillsUtility';

const FooterView = (props) => {
    const [ref, inView] = useInView({
        triggerOnce: true,
        rootMargin: '1%',
    });
    const [iconClass, setIconClass] = useState('');
    const [footerBacklinks, setFooterBacklinks] = useState(props.FooterBacklinks);
    const setShowCookieConsent = useSetRecoilState(atoms.showCookieConsent);
    const routes = useRecoilValue(atoms.routes);

    useEffect(() => {
        if (inView) {
            setIconClass("lazyload-bg");
        }
    }, [inView]);

    const [status, setStatus] = useState({
        mobileOptionBar: false,
        detailContactBar: false
    });
    useEffect(() => {
        setStatus({
            mobileOptionBar: document.getElementsByClassName('mobile-option-bar').length > 0,
            detailContactBar: document.getElementsByClassName('detail-contact-bar').length > 0
        });
    }, []);

    const showCookieConsentModal = () => {
        setShowCookieConsent(true);
    }

    const getBottomPosition = () => {
        let bottom = 0;
        if (status.mobileOptionBar) {
            let mobileOptionBar = document.getElementsByClassName('mobile-option-bar')?.[0];
            if (mobileOptionBar) {
                bottom += mobileOptionBar.offsetHeight + 8;
            }
        }

        if (status.detailContactBar) {
            let detailContactBar = document.getElementsByClassName('detail-contact-bar')?.[0];
            if (detailContactBar) {
                bottom += detailContactBar.offsetHeight;
            }
        }
        if (bottom > 0) return `${bottom}px`;
        return '';
    };

    function toTitleCase(str) {
        return str.toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
    }

    const [stateParam, setStateParam] = useState('');
    const [cityParam, setCityParam] = useState('');
    const [fullLocation, setFullLocation] = useState('');

    const prevLocationRef = useRef({ state: '', city: '' });
    const hasInitializedLocationRef = useRef(false);

    useEffect(() => {

        const updateLocation = () => {
            const state = getURLParameter(window.location.search, "State").split('|')[0];
            const city = getURLParameter(window.location.search, "City");

            setStateParam(state);
            setCityParam(city);

            const fullLocationValue = state ? (city ? `${city}, ${state}` : state) : '';
            setFullLocation(toTitleCase(fullLocationValue));

            const changed =
                prevLocationRef.current.state !== state ||
                prevLocationRef.current.city !== city;

            // Don't update footer backlinks on page load (we get those values from props initally)
            if (!hasInitializedLocationRef.current) {
                hasInitializedLocationRef.current = true;
                prevLocationRef.current = { state, city };
                return;
            }


            if (changed) {
                prevLocationRef.current = { state, city };
                updateFooterBacklinks(state, city);
            }
        };

        updateLocation();

        window.addEventListener("popstate", updateLocation);

        const origPushState = history.pushState.bind(history);
        const origReplaceState = history.replaceState.bind(history);

        history.pushState = (...args) => { origPushState(...args); updateLocation(); };
        history.replaceState = (...args) => { origReplaceState(...args); updateLocation(); };

        return () => {
            window.removeEventListener("popstate", updateLocation);
            history.pushState = origPushState;
            history.replaceState = origReplaceState;
        };
    }, []);

    const updateFooterBacklinks = (state, city) => {
            return sandhillsRequest({
                url: routes.GetFooterBacklinks,
                data: { state, city },
                errorHandling: {
                    message: 'Error retrieving footer backlinks.',
                    showDetails: true,
                    useConsole: true,
                    useAlert: false
                }
            }).then((response) => {
                setFooterBacklinks(response);
            });
        }

    const GetOnClick = (linkId) => {
        if (linkId != '') {
            switch (linkId) {
                case 'updatecookies':
                    showCookieConsentModal();
                    break;
                default:
                    break;
            }
        };

        return null;
    }

    return (
        <footer className='site-footer no-print' ref={ref}>
            <div className='footer-top-border'></div>
            <div className='footer-content' style={{ paddingBottom: getBottomPosition() }}>
                <div className='footer-row-1'>
                    <div className='footer-links'>
                        {props.ShowTopLinks && props.FooterLinks && props.FooterLinks.length > 0 && props.FooterLinks.map(link =>
                            <Fragment key={link.LinkURL}>
                                {link.LinkURL.startsWith('http') ?
                                    <a className='footer-link' href={link.LinkURL} target='_blank' aria-label={link.LinkText + ' (' + translator.translate('sandhills.opensinanewtab') + ')'}  rel='nofollow'>{link.LinkText}</a> :
                                    <a className='footer-link' href={link.LinkURL} rel={link.LinkRelation ? link.LinkRelation : null}>{link.LinkText}</a>}
                            </Fragment>)}
                    </div>
                    <div className='social-media-links'>
                        {props.ShowSocialMediaNetworkLinks &&
                            <div className='media-row'>
                                {props.SocialMediaNetworkLinks && props.SocialMediaNetworkLinks.length > 0 && props.SocialMediaNetworkLinks.map(link =>
                                    <a key={link.LinkURL} href={link.LinkURL} className={'social-item ' + link.LinkText + '-img ' + iconClass} target='_blank' aria-label={link.LinkText + ' (' + translator.translate('sandhills.opensinanewtab') + ')'}>
                                        <LazyLoadImage style={link.LinkText === "youtube" ? { width: '37px', height: '35px', marginLeft: '10px', marginTop: '5px' } : {}} src={link.ImgURL} className='social-image' alt={link.LinkText + " " + translator.translate("sandhills.companylogomediaid")}/>
                                    </a>)}
                            </div>}
                    </div>
                    {props.FooterLogo &&
                        <div className='footer-logo'>
                            <img src={props.FooterLogo} alt="" loading='lazy' />
                        </div>
                    }
                </div>
                <div className='footer-row-2'>
                    <div className='backlinks'>
                        {footerBacklinks && footerBacklinks.length > 0 && footerBacklinks.map(link => {
                            return (<a key={link.LinkURL} className='footer-link' href={link.LinkURL} target={link.LinkTargetName} aria-label={link.LinkTargetName == '_blank' ? link.LinkText + ' (' + translator.translate('sandhills.opensinanewtab') + ')' : link.LinkText}>{(stateParam != '' && link.IncludeLocation) ? translator.translate('sandhills.tradepubwebsite.mvc.inplaceholder').replace('{0}', link.LinkText).replace('{1}', fullLocation) : link.LinkText}</a>);
                        })}
                    </div>
                    {props.ShowOtherLinks &&
                        <div className='other-links'>
                            {props.OtherFooterLinks && props.OtherFooterLinks.length > 0 && props.OtherFooterLinks.map(link => {
                                if (!link.LinkId || link.linkId == '') {
                                    return (<a key={link.LinkURL} className='footer-link' href={link.LinkURL} target={link.LinkTargetName} aria-label={link.LinkTargetName == '_blank' ? link.LinkText + ' (' + translator.translate('sandhills.opensinanewtab') + ')' : link.LinkText}>{link.ImgURL && <img fetchpriority='low' loading='lazy' src={link.ImgURL} alt={link.ImgAltText}></img>}{link.LinkText}</a>);
                                } else {
                                    return (<a role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); GetOnClick(link.LinkId); } }} onClick={() => GetOnClick(link.LinkId)} className='footer-link'>{ link.LinkText}</a>);
                                }
                            })}
                       </div>}
                </div>
                <div className='footer-row-2 center'>
                    <span className='copyright'>{props.Copyright}</span>
                </div>
            </div>
        </footer>
    )
};

export default FooterView;
global.FooterView = FooterView; 